import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams, Link as RouterLink, useParams } from 'react-router-dom';
import {
  Box, Container, Typography, TextField, MenuItem, Button, Paper,
  Chip, Tooltip, Collapse, IconButton, Table, TableBody, TableRow, TableCell,
} from '@mui/material';
import {
  ExpandMore as ExpandIcon,
  ContentCopy as CopyIcon,
  InfoOutlined as InfoIcon,
} from '@mui/icons-material';
import ReferralCTA from '../components/shared/ReferralCTA';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS, CategoryScale, LinearScale, BarElement, Tooltip as CTooltip, Legend,
} from 'chart.js';
import SEOHead from '../components/SEO/SEOHead';
import PageHeader from '../components/Layout/PageHeader';
import FAQSection from '../components/SEO/FAQSection';
import CrossLinks from '../components/Layout/CrossLinks';
import NewsletterSection from '../components/Layout/NewsletterSection';
import { calcNetSalary } from '../data/salaryCalc';
import { PROVINCES } from '../data/taxBrackets';
import { colors } from '../theme/colorTokens';

ChartJS.register(CategoryScale, LinearScale, BarElement, CTooltip, Legend);

const trackEvent = (name, props) => {
  if (window.plausible) window.plausible(name, { props });
  if (window.dataLayer) window.dataLayer.push({ event: name.toLowerCase().replace(/\s+/g, '_'), ...props });
};

const fmt = (n) => n.toLocaleString('en-CA', { style: 'currency', currency: 'CAD', maximumFractionDigits: 0 });

const FREQ_OPTIONS = [
  { value: 'annual', label: 'Anual', divisor: 1 },
  { value: 'monthly', label: 'Mensual', divisor: 12 },
  { value: 'biweekly', label: 'Quincenal', divisor: 26 },
  { value: 'weekly', label: 'Semanal', divisor: 52 },
];

const COMPARE_PROVINCES = ['QC', 'ON', 'AB', 'BC'];

const QC_NOTE = 'Quebec tiene impuestos más altos, pero incluye servicios que en otras provincias pagas aparte: guarderías subsidiadas ($9.10/día), QPIP (licencia parental), y RAMQ (salud con cobertura dental parcial).';

/* ── Tooltip wrapper ── */
const TT = ({ tip, children }) => (
  <Tooltip title={tip} arrow enterDelay={200}>
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, cursor: 'help' }}>
      {children}
      <InfoIcon sx={{ fontSize: 14, color: colors.textSecondary, opacity: 0.6 }} />
    </span>
  </Tooltip>
);

/* ── FAQ data ── */
const faqs = [
  { question: '¿Cuánto impuesto se paga en Quebec con $50,000?', answer: 'Con un salario bruto de $50,000 en Quebec, tu neto estimado es ~$37,450 al año o ~$3,121/mes. Las deducciones incluyen impuesto federal (~$3,600), provincial (~$4,200), QPP (~$3,000), EI (~$527) y QPIP (~$247).' },
  { question: '¿Por qué Quebec tiene impuestos más altos que Ontario?', answer: 'Quebec ofrece más servicios incluidos: guarderías subsidiadas ($9.10/día), QPIP (licencia parental más generosa), RAMQ con cobertura dental parcial, y programas sociales adicionales. Lo que "pagas de más" en impuestos ya incluye cosas que en Ontario pagarías aparte.' },
  { question: '¿Qué es QPP y por qué me lo descuentan?', answer: 'QPP (Quebec Pension Plan) es la contribución obligatoria para tu pensión de retiro en Quebec. Es equivalente al CPP en el resto de Canadá. Te da derecho a recibir una pensión cuando te retires. En 2026, la tasa es 6.40% sobre ingresos entre $3,500 y $71,300.' },
  { question: '¿Qué es QPIP?', answer: 'QPIP (Quebec Parental Insurance Plan) es un seguro exclusivo de Quebec que cubre licencia de maternidad/paternidad. La tasa para empleados es 0.494% sobre ingresos hasta $98,000. Si planeas tener hijos en Quebec, este beneficio vale más de lo que pagas.' },
  { question: '¿Cómo reduzco mis impuestos legalmente?', answer: 'Las formas principales: 1) Aportar al RRSP (reduce tu ingreso gravable), 2) Usar el FHSA si planeas comprar casa ($8,000/año deducible), 3) Maximizar tu TFSA (crecimiento libre de impuestos), 4) Reclamar todas tus deducciones (mudanza, gastos médicos, transporte público en QC).' },
  { question: '¿Mi salario neto cambia si aporto al RRSP?', answer: 'Sí. Cada dólar que aportas al RRSP reduce tu ingreso gravable. Si tu tasa marginal es 30%, un aporte de $5,000 te ahorra ~$1,500 en impuestos. Usa la opción "Avanzado" en la calculadora para ver el impacto.' },
  { question: '¿Cuál es la tasa marginal vs la tasa efectiva?', answer: 'Tasa efectiva = el porcentaje REAL de tu salario total que va a impuestos. Tasa marginal = cuánto pagas por cada dólar EXTRA que ganas. La marginal siempre es más alta porque aplica solo al último tramo de ingreso.' },
  { question: '¿Cuánto gana en promedio un recién llegado en Quebec?', answer: 'Según Statistics Canada, el ingreso mediano de inmigrantes recientes en Quebec es $35,000-$45,000 en los primeros 2 años, subiendo a $50,000-$65,000 después de 5 años dependiendo de la industria y experiencia.' },
];

/* ── Schemas ── */
const webAppSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebApplication',
  name: 'Calculadora de Salario Neto Canadá 2026',
  description: 'Calcula tu salario neto en Québec, Ontario, BC o Alberta.',
  applicationCategory: 'FinanceApplication',
  operatingSystem: 'Web',
  offers: { '@type': 'Offer', price: '0', priceCurrency: 'CAD' },
  dateModified: '2026-03-06',
};

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((f) => ({
    '@type': 'Question',
    name: f.question,
    acceptedAnswer: { '@type': 'Answer', text: f.answer },
  })),
};

/* ── Page ── */
const SalaryCalculatorPage = () => {
  const { t } = useTranslation();
  const { lng } = useParams();
  const [searchParams] = useSearchParams();

  // Inputs
  const [province, setProvince] = useState(searchParams.get('prov')?.toUpperCase() || 'QC');
  const [grossStr, setGrossStr] = useState(searchParams.get('gross') || '');
  const [freq, setFreq] = useState(searchParams.get('freq') || 'monthly');
  const [rrspStr, setRrspStr] = useState(searchParams.get('rrsp') || '');
  const [showAdvanced, setShowAdvanced] = useState(Boolean(searchParams.get('rrsp')));
  const [hasCalculated, setHasCalculated] = useState(false);

  const gross = Number(grossStr) || 0;
  const rrsp = Number(rrspStr) || 0;

  // Track page view
  useEffect(() => {
    trackEvent('Calculator Started', { tool: 'salary' });
  }, []);

  const result = useMemo(() => {
    if (gross <= 0) return null;
    return calcNetSalary(gross, province, rrsp);
  }, [gross, province, rrsp]);

  const handleCalculate = useCallback(() => {
    if (gross > 0) {
      setHasCalculated(true);
      trackEvent('Calculator Complete', { tool: 'salary', province });
    }
  }, [gross, province]);

  // Province compare
  const compareResults = useMemo(() => {
    if (gross <= 0) return [];
    return COMPARE_PROVINCES.map((prov) => ({
      province: prov,
      ...calcNetSalary(gross, prov, rrsp),
    }));
  }, [gross, rrsp]);

  // Stacked bar chart
  const chartData = useMemo(() => {
    if (!result) return null;
    const labels = ['Tu salario'];
    const datasets = [
      { label: 'Tuyo (neto)', data: [result.net], backgroundColor: colors.success },
      { label: 'Federal', data: [result.federalTax], backgroundColor: colors.primary },
      { label: 'Provincial', data: [result.provincialTax], backgroundColor: '#5F8BB0' },
      { label: result.pensionLabel, data: [result.pension], backgroundColor: '#9E9E9E' },
      { label: 'EI', data: [result.ei], backgroundColor: '#BDBDBD' },
    ];
    if (result.qpip > 0) {
      datasets.push({ label: 'QPIP', data: [result.qpip], backgroundColor: '#E0E0E0' });
    }
    return { labels, datasets };
  }, [result]);

  const chartOptions = {
    indexAxis: 'y',
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'bottom', labels: { boxWidth: 12, font: { size: 12 } } },
      tooltip: {
        callbacks: { label: (ctx) => `${ctx.dataset.label}: ${fmt(ctx.raw)}` },
      },
    },
    scales: {
      x: { stacked: true, ticks: { callback: (v) => fmt(v) }, grid: { display: false } },
      y: { stacked: true, display: false },
    },
  };

  // Share URL
  const shareUrl = useMemo(() => {
    if (!gross) return '';
    const base = `${window.location.origin}/${lng}/herramientas/calculadora-salario-neto`;
    const params = new URLSearchParams({ gross: grossStr, prov: province.toLowerCase(), freq });
    if (rrsp > 0) params.set('rrsp', rrspStr);
    return `${base}?${params}`;
  }, [gross, grossStr, province, freq, rrsp, rrspStr, lng]);

  const copyShareLink = useCallback(() => {
    navigator.clipboard?.writeText(shareUrl);
  }, [shareUrl]);

  const freqDivisor = FREQ_OPTIONS.find((f) => f.value === freq)?.divisor || 12;

  return (
    <Box sx={{ bgcolor: '#FFFFFF' }}>
      <SEOHead
        seoKey="salaryCalc"
        title="Calcula tu Salario Neto en Canadá 2026 | FinMaple"
        description="Descubre cuánto te queda después de impuestos según tu provincia y frecuencia de pago. Estimación gratuita para Quebec, Ontario, BC y Alberta."
        canonical="/herramientas/calculadora-salario-neto"
        routeKey="salaryCalc"
        schema={[webAppSchema, faqSchema]}
      />

      <PageHeader
        variant="minimal"
        title="Calcula tu salario neto en Canadá"
        subtitle="Descubre cuánto te queda después de impuestos según tu provincia y frecuencia de pago."
        breadcrumbs={[{ label: 'Herramientas', to: '/herramientas' }, { label: 'Calculadora Salario Neto' }]}
        maxWidth="lg"
      >
        <Box sx={{ display: 'flex', gap: 1, mt: 1.5, flexWrap: 'wrap', justifyContent: 'center' }}>
          <Chip label="Estimación útil" size="small" variant="outlined" sx={{ fontSize: '12px', borderColor: colors.border, color: colors.textSecondary }} />
          <Chip label="Tasas 2026" size="small" variant="outlined" sx={{ fontSize: '12px', borderColor: colors.border, color: colors.textSecondary }} />
          <Chip label="No reemplaza asesoría profesional" size="small" variant="outlined" sx={{ fontSize: '12px', borderColor: colors.border, color: colors.textSecondary }} />
        </Box>
      </PageHeader>

      <Container maxWidth="lg" sx={{ pb: { xs: 4, md: 6 }, mt: { xs: 1, md: 2 } }}>
        {/* Calculator Card */}
        <Paper
          elevation={0}
          sx={{ border: `1px solid ${colors.border}`, borderRadius: '16px', p: { xs: 3, md: 4 }, mb: 4 }}
        >
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 2.5, mb: 3 }}>
            <TextField
              select label="Provincia" value={province}
              onChange={(e) => setProvince(e.target.value)} fullWidth size="small"
            >
              {PROVINCES.map((p) => (
                <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>
              ))}
            </TextField>
            <TextField
              label="Salario bruto anual" type="number" value={grossStr}
              onChange={(e) => setGrossStr(e.target.value)} fullWidth size="small"
              placeholder="55000"
              inputProps={{ min: 0, step: 1000, inputMode: 'decimal' }}
            />
            <TextField
              select label="Frecuencia" value={freq}
              onChange={(e) => setFreq(e.target.value)} fullWidth size="small"
            >
              {FREQ_OPTIONS.map((f) => (
                <MenuItem key={f.value} value={f.value}>{f.label}</MenuItem>
              ))}
            </TextField>
          </Box>

          {/* Advanced */}
          <Box sx={{ mb: 2 }}>
            <Button
              size="small" variant="text"
              onClick={() => setShowAdvanced(!showAdvanced)}
              endIcon={<ExpandIcon sx={{ transform: showAdvanced ? 'rotate(180deg)' : 'none', transition: '0.2s' }} />}
              sx={{ textTransform: 'none', color: colors.textSecondary, fontSize: '13px' }}
            >
              Avanzado
            </Button>
            <Collapse in={showAdvanced}>
              <Box sx={{ mt: 1.5, maxWidth: 300 }}>
                <TextField
                  label="Aporte RRSP anual" type="number" value={rrspStr}
                  onChange={(e) => setRrspStr(e.target.value)} fullWidth size="small"
                  placeholder="0"
                  inputProps={{ min: 0, step: 500, inputMode: 'decimal' }}
                  helperText="Reduce tu ingreso gravable"
                />
              </Box>
            </Collapse>
          </Box>

          <Button
            variant="contained" disableElevation
            onClick={handleCalculate}
            disabled={gross <= 0}
            sx={{
              textTransform: 'none', fontWeight: 600, fontSize: '15px', px: 4, py: 1.2,
              borderRadius: '10px', bgcolor: colors.primary, '&:hover': { bgcolor: '#1A3152' }, minHeight: 44,
              width: { xs: '100%', sm: 'auto' },
            }}
          >
            Calcular
          </Button>
        </Paper>

        {/* Results */}
        {result && hasCalculated && (
          <>
            {/* Hero KPI */}
            <Paper
              elevation={0}
              sx={{
                border: `1px solid ${colors.border}`, borderRadius: '16px',
                p: { xs: 3, md: 4 }, mb: 3, textAlign: 'center',
              }}
            >
              <Typography sx={{ fontSize: '14px', color: colors.textSecondary, mb: 0.5 }}>
                Tu salario neto estimado ({FREQ_OPTIONS.find((f) => f.value === freq)?.label.toLowerCase()})
              </Typography>
              <Typography
                sx={{
                  fontSize: { xs: '40px', md: '52px' }, fontWeight: 800,
                  color: colors.primary, lineHeight: 1.1, fontVariantNumeric: 'tabular-nums',
                }}
              >
                {fmt(Math.round(result.net / freqDivisor))}
              </Typography>
              <Typography sx={{ fontSize: '13px', color: colors.textSecondary, mt: 1 }}>
                Neto anual: {fmt(result.net)} · Bruto: {fmt(result.gross)}
              </Typography>

              {/* Other frequencies */}
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: { xs: 1.5, md: 3 }, mt: 2, flexWrap: 'wrap' }}>
                {FREQ_OPTIONS.filter((f) => f.value !== freq).map((f) => (
                  <Box key={f.value} sx={{ textAlign: 'center' }}>
                    <Typography sx={{ fontSize: '12px', color: colors.textSecondary }}>{f.label}</Typography>
                    <Typography sx={{ fontSize: '16px', fontWeight: 600, color: colors.textPrimary, fontVariantNumeric: 'tabular-nums' }}>
                      {fmt(Math.round(result.net / f.divisor))}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Paper>

            {/* Deduction Table + Chart */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3, mb: 4 }}>
              {/* Table */}
              <Paper
                elevation={0}
                sx={{ border: `1px solid ${colors.border}`, borderRadius: '14px', p: { xs: 2, md: 3 }, overflow: 'auto' }}
              >
                <Typography sx={{ fontWeight: 600, fontSize: '16px', color: colors.textPrimary, mb: 2 }}>
                  Desglose de deducciones
                </Typography>
                <Table size="small">
                  <TableBody>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Salario bruto</TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{fmt(result.gross)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Impuesto federal</TableCell>
                      <TableCell align="right" sx={{ color: colors.error, fontVariantNumeric: 'tabular-nums' }}>-{fmt(result.federalTax)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Impuesto provincial</TableCell>
                      <TableCell align="right" sx={{ color: colors.error, fontVariantNumeric: 'tabular-nums' }}>-{fmt(result.provincialTax)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>
                        <TT tip={province === 'QC' ? 'Contribución obligatoria para tu pensión de retiro en Quebec' : 'Contribución obligatoria para tu pensión de retiro en Canadá'}>
                          {result.pensionLabel}
                        </TT>
                      </TableCell>
                      <TableCell align="right" sx={{ color: colors.error, fontVariantNumeric: 'tabular-nums' }}>-{fmt(result.pension)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>
                        <TT tip="Seguro de empleo. Te protege si pierdes el trabajo.">EI</TT>
                      </TableCell>
                      <TableCell align="right" sx={{ color: colors.error, fontVariantNumeric: 'tabular-nums' }}>-{fmt(result.ei)}</TableCell>
                    </TableRow>
                    {result.qpip > 0 && (
                      <TableRow>
                        <TableCell>
                          <TT tip="Seguro parental de Quebec. Maternidad/paternidad.">QPIP</TT>
                        </TableCell>
                        <TableCell align="right" sx={{ color: colors.error, fontVariantNumeric: 'tabular-nums' }}>-{fmt(result.qpip)}</TableCell>
                      </TableRow>
                    )}
                    <TableRow sx={{ '& td': { borderTop: `2px solid ${colors.border}`, fontWeight: 700 } }}>
                      <TableCell>Salario neto</TableCell>
                      <TableCell align="right" sx={{ color: colors.success, fontVariantNumeric: 'tabular-nums' }}>{fmt(result.net)}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>

                {/* Rates */}
                <Box sx={{ display: 'flex', gap: 3, mt: 2, pt: 2, borderTop: `1px solid ${colors.border}` }}>
                  <Box>
                    <TT tip="Porcentaje total de tu salario que va a impuestos y deducciones. Tu tasa real.">
                      <Typography sx={{ fontSize: '12px', color: colors.textSecondary }}>Tasa efectiva</Typography>
                    </TT>
                    <Typography sx={{ fontSize: '18px', fontWeight: 600, color: colors.textPrimary }}>{result.effectiveRate}%</Typography>
                  </Box>
                  <Box>
                    <TT tip="De cada dólar extra que ganas, cuánto se queda el gobierno.">
                      <Typography sx={{ fontSize: '12px', color: colors.textSecondary }}>Tasa marginal</Typography>
                    </TT>
                    <Typography sx={{ fontSize: '18px', fontWeight: 600, color: colors.textPrimary }}>{result.marginalRate}%</Typography>
                  </Box>
                </Box>

                {rrsp > 0 && result.rrspTaxSaving > 0 && (
                  <Box sx={{ mt: 2, p: 1.5, bgcolor: '#E8F5E9', borderRadius: '8px' }}>
                    <Typography sx={{ fontSize: '13px', color: colors.success, fontWeight: 600 }}>
                      Tu aporte RRSP de {fmt(rrsp)} te ahorra ~{fmt(result.rrspTaxSaving)} en impuestos
                    </Typography>
                  </Box>
                )}
              </Paper>

              {/* Chart */}
              {chartData && (
                <Paper
                  elevation={0}
                  sx={{ border: `1px solid ${colors.border}`, borderRadius: '14px', p: { xs: 2, md: 3 } }}
                >
                  <Typography sx={{ fontWeight: 600, fontSize: '16px', color: colors.textPrimary, mb: 2 }}>
                    ¿A dónde va tu salario?
                  </Typography>
                  <Box sx={{ height: 200 }}>
                    <Bar data={chartData} options={chartOptions} />
                  </Box>
                </Paper>
              )}
            </Box>

            {/* QC Note */}
            {province === 'QC' && (
              <Box sx={{ bgcolor: '#FFF8E1', border: '1px solid rgba(249,168,37,0.3)', borderRadius: '12px', p: 2.5, mb: 4 }}>
                <Typography sx={{ fontSize: '14px', color: colors.textSecondary, lineHeight: 1.7 }}>
                  💡 {QC_NOTE}
                </Typography>
              </Box>
            )}

            {/* Interpretation */}
            <Paper
              elevation={0}
              sx={{ border: `1px solid ${colors.border}`, borderRadius: '14px', p: { xs: 2.5, md: 3 }, mb: 4 }}
            >
              <Typography variant="h6" component="h3" sx={{ fontWeight: 600, fontSize: '16px', color: colors.textPrimary, mb: 1.5 }}>
                Qué significa esto para ti
              </Typography>
              <Typography sx={{ fontSize: '14px', color: colors.textSecondary, lineHeight: 1.8, mb: 1 }}>
                • De tu salario bruto de {fmt(result.gross)}, te quedan <strong>{fmt(result.monthly)}/mes</strong> en tu cuenta.
              </Typography>
              <Typography sx={{ fontSize: '14px', color: colors.textSecondary, lineHeight: 1.8, mb: 1 }}>
                • El {result.effectiveRate}% se va en impuestos y contribuciones obligatorias.
              </Typography>
              {rrsp > 0 && (
                <Typography sx={{ fontSize: '14px', color: colors.textSecondary, lineHeight: 1.8 }}>
                  • Tu aporte RRSP reduce tu factura fiscal. Sin RRSP, tu neto sería ~{fmt(calcNetSalary(gross, province, 0).net)}.
                </Typography>
              )}
              <Typography sx={{ fontSize: '14px', color: colors.textSecondary, lineHeight: 1.8, fontStyle: 'italic', mt: 1 }}>
                Esta es una estimación. Tu neto real puede variar según deducciones adicionales, beneficios del empleador y créditos fiscales.
              </Typography>
            </Paper>

            {/* Province Compare */}
            {compareResults.length > 0 && (
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" component="h3" sx={{ fontWeight: 600, fontSize: '16px', color: colors.textPrimary, mb: 2 }}>
                  ¿Cómo cambia en otra provincia?
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 2 }}>
                  {compareResults.map((cr) => (
                    <Paper
                      key={cr.province}
                      elevation={0}
                      sx={{
                        border: `1px solid ${cr.province === province ? colors.primary : colors.border}`,
                        borderRadius: '12px', p: 2, textAlign: 'center',
                        bgcolor: cr.province === province ? 'rgba(30,58,95,0.02)' : '#fff',
                      }}
                    >
                      <Typography sx={{ fontSize: '13px', fontWeight: 600, color: colors.textPrimary, mb: 0.5 }}>
                        {cr.province}
                      </Typography>
                      <Typography sx={{ fontSize: '20px', fontWeight: 700, color: colors.primary, fontVariantNumeric: 'tabular-nums' }}>
                        {fmt(cr.monthly)}
                      </Typography>
                      <Typography sx={{ fontSize: '11px', color: colors.textSecondary }}>/mes neto</Typography>
                      <Typography sx={{ fontSize: '11px', color: colors.textSecondary, mt: 0.5 }}>
                        Efectiva: {cr.effectiveRate}%
                      </Typography>
                    </Paper>
                  ))}
                </Box>
              </Box>
            )}

            {/* RRSP Tip */}
            {rrsp === 0 && (
              <Box sx={{ bgcolor: colors.softBg, borderRadius: '12px', p: { xs: 2.5, md: 3 }, mb: 4, border: `1px solid ${colors.border}` }}>
                <Typography sx={{ fontWeight: 600, fontSize: '15px', color: colors.textPrimary, mb: 0.5 }}>
                  💡 ¿Sabías que puedes pagar menos impuestos?
                </Typography>
                <Typography sx={{ fontSize: '14px', color: colors.textSecondary, lineHeight: 1.7 }}>
                  Si aportas al RRSP, reduces tu ingreso gravable. Con tu tasa marginal de {result.marginalRate}%, un aporte de $5,000 te ahorraría ~{fmt(5000 * parseFloat(result.marginalRate) / 100)} en impuestos.
                  Usa la opción "Avanzado" arriba para ver el impacto.
                </Typography>
              </Box>
            )}

            {/* Wealthsimple CTA */}
            <Box sx={{ bgcolor: '#F7F9FC', borderRadius: '12px', p: { xs: 2.5, md: 3 }, mb: 4, border: '1px solid #EEF0F3', textAlign: 'center' }}>
              <Typography sx={{ fontWeight: 600, fontSize: '15px', color: '#1A1A1A', mb: 0.5 }}>
                {lng === 'fr' ? 'Prêt à investir la différence\u00a0?' : lng === 'en' ? 'Ready to invest the difference?' : '¿Listo para invertir la diferencia?'}
              </Typography>
              <Typography sx={{ fontSize: '14px', color: '#5F6B7A', lineHeight: 1.7, mb: 2 }}>
                {lng === 'fr' ? 'Ouvrez un CÉLI ou un REER avec Wealthsimple et faites fructifier votre argent sans frais cachés.' : lng === 'en' ? 'Open a TFSA or RRSP with Wealthsimple and grow your money with no hidden fees.' : 'Abre una TFSA o RRSP con Wealthsimple y haz crecer tu dinero sin comisiones ocultas.'}
              </Typography>
              <ReferralCTA bank="wealthsimple">
                {lng === 'fr' ? 'Ouvrir un compte Wealthsimple' : lng === 'en' ? 'Open a Wealthsimple account' : 'Abrir cuenta Wealthsimple'}
              </ReferralCTA>
            </Box>

            {/* Share + Next Steps */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 4, alignItems: 'center' }}>
              <Button
                variant="outlined" size="small"
                startIcon={<CopyIcon />}
                onClick={copyShareLink}
                sx={{ textTransform: 'none', borderColor: colors.border, color: colors.textSecondary, borderRadius: '10px' }}
              >
                Copiar enlace de mi cálculo
              </Button>
            </Box>

            {/* Next action cards */}
            <Typography variant="h6" component="h3" sx={{ fontWeight: 600, fontSize: '16px', color: colors.textPrimary, mb: 2 }}>
              Tu siguiente mejor paso
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 2, mb: 4 }}>
              {[
                { title: 'Compara RRSP vs TFSA', text: 'Descubre cuál cuenta te conviene según tu situación.', cta: 'Ir a calculadora', to: '/herramientas/calculadora-rrsp-vs-tfsa' },
                { title: 'Lee la guía de impuestos', text: 'Entiende qué créditos puedes reclamar para pagar menos.', cta: 'Ver guía', to: '/blog/guia-impuestos-latinos-quebec-2026' },
                { title: 'Costo de vida en Montreal', text: 'Compara tu neto con lo que realmente cuesta vivir.', cta: 'Ver artículo', to: '/blog/costo-vida-montreal-inmigrantes-2026' },
              ].map((card, i) => (
                <Paper
                  key={i}
                  component={RouterLink}
                  to={`/${lng}${card.to}`}
                  elevation={0}
                  sx={{
                    border: `1px solid ${colors.border}`, borderRadius: '12px', p: 2.5,
                    textDecoration: 'none', transition: 'all 200ms ease',
                    '&:hover': { borderColor: colors.primary, boxShadow: '0 4px 16px rgba(0,0,0,0.06)' },
                  }}
                >
                  <Typography sx={{ fontWeight: 600, fontSize: '15px', color: colors.textPrimary, mb: 0.5 }}>{card.title}</Typography>
                  <Typography sx={{ fontSize: '13px', color: colors.textSecondary, lineHeight: 1.6, mb: 1.5 }}>{card.text}</Typography>
                  <Typography sx={{ fontSize: '13px', fontWeight: 600, color: colors.primary }}>{card.cta} →</Typography>
                </Paper>
              ))}
            </Box>

            {/* Email capture */}
            <Paper
              elevation={0}
              sx={{ p: 3, borderRadius: '14px', bgcolor: colors.softBg, border: `1px solid ${colors.border}`, textAlign: 'center', mb: 4 }}
            >
              <Typography sx={{ fontWeight: 600, fontSize: '16px', color: colors.textPrimary, mb: 0.5 }}>
                ¿Quieres recibir tu resumen y una guía de deducciones?
              </Typography>
              <Typography sx={{ fontSize: '14px', color: colors.textSecondary, mb: 2 }}>
                Te enviamos un PDF con tu cálculo y tips para pagar menos impuestos.
              </Typography>
              <Box
                component="form"
                action="https://buttondown.com/api/emails/embed-subscribe/Finmaple"
                method="post"
                target="_blank"
                sx={{ display: 'flex', gap: 1, maxWidth: 400, mx: 'auto', flexDirection: { xs: 'column', sm: 'row' } }}
              >
                <input type="hidden" name="tag" value="salary-calc" />
                <Box
                  component="input" name="email" type="email" placeholder="tu@email.com" required
                  inputMode="email"
                  autoComplete="email"
                  sx={{
                    flex: 1, px: 2, py: 1.5, border: `1px solid ${colors.border}`, borderRadius: '10px',
                    fontSize: '16px', outline: 'none', minHeight: 44, '&:focus': { borderColor: colors.primary },
                  }}
                />
                <Button
                  type="submit" variant="contained" disableElevation
                  onClick={() => trackEvent('Newsletter Submit', { source: 'salary_calculator' })}
                  sx={{
                    textTransform: 'none', fontWeight: 600, fontSize: '14px', px: 3, py: 1.5,
                    borderRadius: '10px', bgcolor: '#D61F2C', '&:hover': { bgcolor: '#B71C1C' }, whiteSpace: 'nowrap',
                  }}
                >
                  Enviar por email
                </Button>
              </Box>
            </Paper>
          </>
        )}

        {/* FAQ + Cross-links */}
        <FAQSection faqs={faqs} />
        <CrossLinks
          title="Siguiente paso"
          links={[
            { label: 'Calculadora RRSP vs TFSA', to: '/herramientas/calculadora-rrsp-vs-tfsa' },
            { label: 'Guía impuestos Quebec 2026', to: '/blog/guia-impuestos-latinos-quebec-2026' },
            { label: 'Construir crédito', to: '/blog/construir-credito-canada-desde-cero' },
            { label: 'Mejores cuentas sin fees', to: '/comparar-cuentas' },
          ]}
        />
      </Container>

      <NewsletterSection />
    </Box>
  );
};

export default SalaryCalculatorPage;
